import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { type BackState, useBackState } from '@/lib/back-nav';
import { plural } from '@/lib/plural';

import { pendingBadge } from '../presentation';
import type { PendingItem } from '../types';
import { usePendingSignatures } from '../use-dashboard';

// Pendientes de firma (so=430, spec §3.4; so=410 §3.3): flujos en aprobación sin firmar,
// lo más urgente primero. Cada fila abre el Editor, donde se firma; "Ver todos y firmar"
// abre la Lista Maestra en el segmento Por firmar (spec D4), con el mismo criterio.

function PendingRow({ item, from }: { item: PendingItem; from: BackState }): ReactElement {
  const badge = pendingBadge(item);
  return (
    <li>
      <Link to={`/documentos/${item.entry.id}/editor`} state={from} className="rowb pend-row">
        <span className="code">{item.entry.code}</span>
        <span className="pend-title">
          {item.entry.title ?? 'Sin título'}
          {/* G2-8: quién falta por firmar (solo con aprobadores asignados). */}
          {item.signers.pending.length > 0 ? (
            <span className="pend-who">Falta {item.signers.pending.join(', ')}</span>
          ) : null}
        </span>
        {item.signers.mine ? <span className="badge pend-mine">Te toca firmar</span> : null}
        <span className={cn('badge', badge.cls)}>{badge.label}</span>
      </Link>
    </li>
  );
}

export function PendingSignaturesCard({ companyId }: { companyId: string }): ReactElement {
  const query = usePendingSignatures({ companyId, enabled: true });
  const total = query.data?.total;
  const fromHere = useBackState('Panel del QMS');
  return (
    <CardFrame
      title="Pendientes de firma"
      count={total === undefined ? undefined : plural(total, 'documento', 'documentos')}
    >
      <QueryBody
        query={query}
        rows={4}
        isEmpty={(d) => d.items.length === 0}
        emptyText="No hay documentos esperando firma."
      >
        {(d) => (
          <>
            <ul className="pend-list">
              {d.items.map((item) => (
                <PendingRow key={item.flowInstanceId} item={item} from={fromHere} />
              ))}
            </ul>
            <div className="dash-foot">
              {d.items.some((i) => i.signers.mine) ? (
                <Link to="/documentos?estado=mis_firmas" className="btn ghost sm">
                  Ver los que me toca firmar
                </Link>
              ) : null}
              <Link to="/documentos?estado=por_firmar" className="btn ghost sm">
                Ver todos y firmar
              </Link>
            </div>
          </>
        )}
      </QueryBody>
    </CardFrame>
  );
}
